import { getOr, pick, isEmpty } from 'lodash/fp';
import pluralize from 'pluralize';
import moment from 'moment';

import { LOCATIONS_ALL_ID } from '../../../containers/BrandBarber/constants';
import config from '../../../../../../config';

const getEmptySchedule = () => ({ current: {}, future: {} });

export const getLocationSchedule = (barberDataByLocationId, locationId) => {
  if (!locationId || locationId === LOCATIONS_ALL_ID) {
    return getEmptySchedule();
  }

  return getOr(getEmptySchedule(), `${locationId}.schedules`, barberDataByLocationId);
};

export const getAllLocationsSchedules = (barberDataByLocationId, exceptLocationIds = []) =>
  Object.values(barberDataByLocationId)
    .filter(barberData => !exceptLocationIds.includes(barberData.shopId))
    .map(pick(['shopId', 'schedules']));


/**
 * Calculate time ranges minutes amount
 * @return {Number} Minutes amount
 * @param {Array.<{ startTime: string, endTime: string, enabled: boolean }>} ranges - array of time ranges, times formatted as "9:00 am"
 */
export const calculateRangesTimeAmount = (ranges) => {
  if (isEmpty(ranges)) {
    return 0;
  }

  return ranges.reduce((amount, item) => {
    if (!item.enabled) {
      return amount;
    }

    const startTime = moment(item.startTime, config.timeFormat);
    const endTime = moment(item.endTime, config.timeFormat);
    const diffMinutesDuration = moment.duration(endTime.diff(startTime)).asMinutes();

    return amount + diffMinutesDuration;
  }, 0);
};

/**
 * Calculate hours and minutes amount of shops schedules arrays of time ranges
 * @return {{ hours, minutes }} Amount of hours and minutes
 * @param {Array.<{ startDate: Date, workdays: Array.<{ startTime: string, endTime: string, enabled: boolean }> }>} schedules
 */
export const calculateScheduleHoursAndMinutes = (schedules) => {
  if (isEmpty(schedules)) {
    return 0;
  }

  const minutesAmount = schedules.reduce(
    (amount, schedule) =>
      amount + (schedule.startDate ? calculateRangesTimeAmount(schedule.workdays) : 0),
    0,
  );

  const hours = Math.floor(minutesAmount / 60);
  const minutes = minutesAmount % 60;

  return { hours, minutes };
};

/**
 * Calculate and format hours and minutes amount of shops schedules time ranges
 * @return {String} Amount of hours and minutes
 * @param {{ hours, minutes }} totalWorkingHours
 */
export const formatScheduleTimeSummary = (totalWorkingHours) => {
  if (!totalWorkingHours || (!totalWorkingHours.hours && !totalWorkingHours.minutes)) {
    return '-';
  }

  const { hours, minutes } = totalWorkingHours;
  return `${hours ? pluralize('hour', hours, true) : ''} ${minutes ? pluralize('minute', minutes, true) : ''}`.trim();
};

/**
 * Calculate whether shop summary has any enabled workdays
 * @return {boolean} whether shop summary has any enabled workdays
 * @param { workdays: Array.<{ enabled: boolean }> } schedule
 */
export const scheduleHasEnabledDays = schedule =>
  schedule.workdays && schedule.workdays.some(workday => workday.enabled);

const formatScheduleDate = date => moment(date).format(config.dateFormat);

export const formatScheduleDateRange = (startDate, endDate) =>
  `${formatScheduleDate(startDate)} - ${endDate ? formatScheduleDate(endDate) : 'No End Date'}`;
